import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Badge,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import {
  ArrowLeft, BarChart3, Plus, Trash2, Edit2, DollarSign,
  RefreshCw, X, Save, FolderOpen, CheckCircle, AlertCircle, Search,
  TrendingUp, TrendingDown, Calendar, PieChart, Download, CloudUpload
} from 'lucide-react';

const BudgetAgentV2Detail = () => {
  const navigate = useNavigate();
  const { budgetId } = useParams();

  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Budget vs Actual state
  const [budgetVsActual, setBudgetVsActual] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loadingActuals, setLoadingActuals] = useState(false);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('ytd');
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly', 'vsActual', or 'details'

  // Month and Year filter for monthly view
  const [startMonth, setStartMonth] = useState('Jan');
  const [endMonth, setEndMonth] = useState('Dec');
  const [selectedYear, setSelectedYear] = useState(null); // Will be set from budget's fiscal_year
  const allMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const availableYears = ['2024', '2025', '2026', '2027'];

  // Edit states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);

  // Add expense modal
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseCategoryId, setExpenseCategoryId] = useState(null);
  const [newExpense, setNewExpense] = useState({
    name: '', amount: '', account_id: '',
    jan_budget: '', feb_budget: '', mar_budget: '',
    apr_budget: '', may_budget: '', jun_budget: '',
    jul_budget: '', aug_budget: '', sep_budget: '',
    oct_budget: '', nov_budget: '', dec_budget: ''
  });

  // Financial Summary Budget modal
  const [showFinancialSummaryModal, setShowFinancialSummaryModal] = useState(false);
  const [financialSummaryBudgets, setFinancialSummaryBudgets] = useState({
    income: { jan: '', feb: '', mar: '', apr: '', may: '', jun: '', jul: '', aug: '', sep: '', oct: '', nov: '', dec: '' },
    cogs: { jan: '', feb: '', mar: '', apr: '', may: '', jun: '', jul: '', aug: '', sep: '', oct: '', nov: '', dec: '' }
  });

  // Excel Export state
  const [exporting, setExporting] = useState(false);
  const [exportingBudgetOnly, setExportingBudgetOnly] = useState(false);
  const [exportingActualVsActual, setExportingActualVsActual] = useState(false);
  const [showActualVsActualModal, setShowActualVsActualModal] = useState(false);
  const [actualVsActualPeriod1, setActualVsActualPeriod1] = useState('Q1');
  const [actualVsActualPeriod2, setActualVsActualPeriod2] = useState('Q2');
  const [loadingFinancialBudgets, setLoadingFinancialBudgets] = useState(false);

  // NetSuite Sync state
  const [syncing, setSyncing] = useState(false);

  // Add category modal
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    budget_amount: '',
    jan_budget: '', feb_budget: '', mar_budget: '',
    apr_budget: '', may_budget: '', jun_budget: '',
    jul_budget: '', aug_budget: '', sep_budget: '',
    oct_budget: '', nov_budget: '', dec_budget: ''
  });
  const [selectedCategoryAccounts, setSelectedCategoryAccounts] = useState([]);
  const [categoryAccountSearch, setCategoryAccountSearch] = useState('');
  const [categoryAccountType, setCategoryAccountType] = useState('all');

  // Edit category modal
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [editCategoryData, setEditCategoryData] = useState(null);
  const [editCategoryAccounts, setEditCategoryAccounts] = useState([]); // Accounts to add
  const [editCategoryExistingAccounts, setEditCategoryExistingAccounts] = useState([]); // Already linked accounts
  const [editAccountSearch, setEditAccountSearch] = useState('');

  // NetSuite expense accounts
  const [expenseAccounts, setExpenseAccounts] = useState({ summary: [], detail: [], all: [] });
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [showAccountType, setShowAccountType] = useState('all'); // 'all', 'summary', 'detail'

  useEffect(() => {
    if (budgetId) {
      fetchBudget();
      fetchBudgetVsActual();
      fetchMonthlyData();
    }
  }, [budgetId]);

  // Refetch actuals when period changes
  useEffect(() => {
    if (budgetId && budget) {
      fetchBudgetVsActual();
    }
  }, [selectedPeriod]);

  // Set selectedYear from budget's fiscal_year when budget loads
  useEffect(() => {
    if (budget?.fiscal_year && !selectedYear) {
      setSelectedYear(budget.fiscal_year);
    }
  }, [budget]);

  // Refetch monthly data when year changes
  useEffect(() => {
    if (budgetId && selectedYear) {
      fetchMonthlyData(selectedYear);
    }
  }, [selectedYear]);

  // Fetch expense accounts when add expense or add category modal opens
  useEffect(() => {
    if ((showAddExpense || showAddCategory) && expenseAccounts.all.length === 0) {
      fetchExpenseAccounts();
    }
  }, [showAddExpense, showAddCategory]);

  const fetchBudgetVsActual = async () => {
    try {
      setLoadingActuals(true);
      const response = await apiClient.get(`/api/budget-v2/budgets/${budgetId}/vs-actual?period=${selectedPeriod}`);
      if (response && response.success) {
        setBudgetVsActual(response);
      }
    } catch (error) {
      console.error('Error fetching budget vs actual:', error);
    } finally {
      setLoadingActuals(false);
    }
  };

  const fetchMonthlyData = async (year = null) => {
    try {
      setLoadingMonthly(true);
      const yearParam = year || selectedYear || budget?.fiscal_year || new Date().getFullYear();
      const response = await apiClient.get(`/api/budget-v2/budgets/${budgetId}/vs-actual/monthly?year=${yearParam}`);
      if (response && response.success) {
        setMonthlyData(response);
      }
    } catch (error) {
      console.error('Error fetching monthly data:', error);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const fetchExpenseAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await apiClient.get('/api/budget-v2/netsuite/expense-accounts/all');
      if (response && response.success) {
        setExpenseAccounts({
          summary: response.summary_accounts || [],
          detail: response.detail_accounts || [],
          all: response.all_accounts || []
        });
      }
    } catch (error) {
      console.error('Error fetching expense accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Export Budget vs Actual to Excel
  const exportToExcel = async () => {
    try {
      setExporting(true);
      const yearParam = selectedYear || budget?.fiscal_year || new Date().getFullYear();

      // Use fetch directly to handle blob response
      // Get token from cookie (same as auth.js)
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };
      const token = getCookie('sankalvax_token');

      const response = await fetch(`/api/budget-v2/budgets/${budgetId}/export/excel?year=${yearParam}&start_month=${startMonth}&end_month=${endMonth}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Budget_vs_Actual_${budget?.name || 'Report'}_${yearParam}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Export Budget Only (without actuals) to Excel
  const exportBudgetOnly = async () => {
    try {
      setExportingBudgetOnly(true);

      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };
      const token = getCookie('sankalvax_token');

      const response = await fetch(`/api/budget-v2/budgets/${budgetId}/export/budget-only`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${budget?.name || 'Budget'}_${budget?.fiscal_year || 'Export'}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error exporting budget:', error);
      alert('Failed to export budget. Please try again.');
    } finally {
      setExportingBudgetOnly(false);
    }
  };

  // Export Actual vs Actual to Excel
  const exportActualVsActual = async () => {
    try {
      setExportingActualVsActual(true);
      setShowActualVsActualModal(false);

      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };
      const token = getCookie('sankalvax_token');

      const yearParam = selectedYear || budget?.fiscal_year || new Date().getFullYear();
      const response = await fetch(`/api/budget-v2/budgets/${budgetId}/export/actual-vs-actual?year=${yearParam}&period1=${actualVsActualPeriod1}&period2=${actualVsActualPeriod2}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Actual_vs_Actual_${actualVsActualPeriod1}_vs_${actualVsActualPeriod2}_${budget?.name || 'Budget'}_${yearParam}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error exporting Actual vs Actual:', error);
      alert('Failed to export. Please try again.');
    } finally {
      setExportingActualVsActual(false);
    }
  };

  // Sync Budget to NetSuite
  const syncToNetSuite = async () => {
    try {
      setSyncing(true);
      const response = await apiClient.post(`/api/budget-v2/budgets/${budgetId}/sync-to-netsuite`);
      if (response.data.success) {
        alert(`Successfully synced to NetSuite!\n${response.data.message || ''}`);
      } else {
        alert(`Sync failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error syncing to NetSuite:', error);
      alert(`Sync failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const getFilteredAccounts = () => {
    let accounts = [];
    if (showAccountType === 'summary') {
      accounts = expenseAccounts.summary;
    } else if (showAccountType === 'detail') {
      accounts = expenseAccounts.detail;
    } else {
      accounts = expenseAccounts.all;
    }

    if (!accountSearch.trim()) return accounts;

    const search = accountSearch.toLowerCase();
    return accounts.filter(acc =>
      (acc.acctname || '').toLowerCase().includes(search) ||
      (acc.acctnumber || '').toLowerCase().includes(search)
    );
  };

  const selectAccount = (account) => {
    setNewExpense({
      ...newExpense,
      name: account.acctname || account.acctnumber,
      account_id: account.id
    });
    setAccountSearch('');
  };

  const fetchBudget = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/budget-v2/budgets/${budgetId}`);
      if (response && response.budget) {
        setBudget(response.budget);
      }
    } catch (error) {
      console.error('Error fetching budget:', error);
      alert('Failed to load budget');
      navigate('/budget-agent-v2');
    } finally {
      setLoading(false);
    }
  };

  const addCategory = async () => {
    if (!newCategory.name.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      setSaving(true);

      // Create category first with monthly budgets (convert from millions to dollars)
      const toMillions = (val) => (parseFloat(val) || 0) * 1000000;
      const response = await apiClient.post(`/api/budget-v2/budgets/${budgetId}/categories`, {
        name: newCategory.name,
        description: newCategory.description,
        budget_amount: toMillions(newCategory.budget_amount),
        jan_budget: toMillions(newCategory.jan_budget),
        feb_budget: toMillions(newCategory.feb_budget),
        mar_budget: toMillions(newCategory.mar_budget),
        apr_budget: toMillions(newCategory.apr_budget),
        may_budget: toMillions(newCategory.may_budget),
        jun_budget: toMillions(newCategory.jun_budget),
        jul_budget: toMillions(newCategory.jul_budget),
        aug_budget: toMillions(newCategory.aug_budget),
        sep_budget: toMillions(newCategory.sep_budget),
        oct_budget: toMillions(newCategory.oct_budget),
        nov_budget: toMillions(newCategory.nov_budget),
        dec_budget: toMillions(newCategory.dec_budget)
      });

      // If accounts were selected, add them to the category
      // Backend returns { success: true, category_id: "uuid" }
      const categoryId = response?.category_id;
      if (categoryId && selectedCategoryAccounts.length > 0) {
        for (const account of selectedCategoryAccounts) {
          const accountName = account.acctname || account.fullname || account.name;
          await apiClient.post(`/api/budget-v2/categories/${categoryId}/expenses`, {
            name: accountName,  // Required field
            account_id: account.id,
            account_name: accountName,
            account_number: account.acctnumber,
            amount: 0
          });
        }
      }

      setShowAddCategory(false);
      setNewCategory({
        name: '', description: '', budget_amount: '',
        jan_budget: '', feb_budget: '', mar_budget: '',
        apr_budget: '', may_budget: '', jun_budget: '',
        jul_budget: '', aug_budget: '', sep_budget: '',
        oct_budget: '', nov_budget: '', dec_budget: ''
      });
      setSelectedCategoryAccounts([]);
      setCategoryAccountSearch('');
      fetchBudget();
      fetchMonthlyData();
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category');
    } finally {
      setSaving(false);
    }
  };

  const updateCategory = async (categoryId, data) => {
    try {
      setSaving(true);
      await apiClient.put(`/api/budget-v2/categories/${categoryId}`, data);
      setEditingCategory(null);
      fetchBudget();
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category and all its expenses?')) return;

    try {
      await apiClient.delete(`/api/budget-v2/categories/${categoryId}`);
      fetchBudget();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const addExpense = async () => {
    if (!newExpense.name.trim()) {
      alert('Please enter expense name');
      return;
    }

    // Convert from millions to dollars
    const toMillions = (val) => (parseFloat(val) || 0) * 1000000;

    // Calculate total from monthly budgets (in dollars)
    const totalAmount = ['jan_budget', 'feb_budget', 'mar_budget', 'apr_budget',
      'may_budget', 'jun_budget', 'jul_budget', 'aug_budget',
      'sep_budget', 'oct_budget', 'nov_budget', 'dec_budget']
      .reduce((sum, k) => sum + toMillions(newExpense[k]), 0);

    try {
      setSaving(true);
      await apiClient.post(`/api/budget-v2/categories/${expenseCategoryId}/expenses`, {
        category_id: expenseCategoryId,
        name: newExpense.name,
        amount: totalAmount,
        account_id: newExpense.account_id || null,
        jan_budget: toMillions(newExpense.jan_budget),
        feb_budget: toMillions(newExpense.feb_budget),
        mar_budget: toMillions(newExpense.mar_budget),
        apr_budget: toMillions(newExpense.apr_budget),
        may_budget: toMillions(newExpense.may_budget),
        jun_budget: toMillions(newExpense.jun_budget),
        jul_budget: toMillions(newExpense.jul_budget),
        aug_budget: toMillions(newExpense.aug_budget),
        sep_budget: toMillions(newExpense.sep_budget),
        oct_budget: toMillions(newExpense.oct_budget),
        nov_budget: toMillions(newExpense.nov_budget),
        dec_budget: toMillions(newExpense.dec_budget)
      });

      setShowAddExpense(false);
      setExpenseCategoryId(null);
      setNewExpense({
        name: '', amount: '', account_id: '',
        jan_budget: '', feb_budget: '', mar_budget: '',
        apr_budget: '', may_budget: '', jun_budget: '',
        jul_budget: '', aug_budget: '', sep_budget: '',
        oct_budget: '', nov_budget: '', dec_budget: ''
      });
      setAccountSearch('');
      fetchBudget();
      fetchMonthlyData();
    } catch (error) {
      console.error('Error adding expense:', error);
      alert('Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const updateExpense = async (expenseId, data) => {
    try {
      setSaving(true);
      await apiClient.put(`/api/budget-v2/expenses/${expenseId}`, data);
      setEditingExpense(null);
      fetchBudget();
    } catch (error) {
      console.error('Error updating expense:', error);
      alert('Failed to update expense');
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;

    try {
      await apiClient.delete(`/api/budget-v2/expenses/${expenseId}`);
      fetchBudget();
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Failed to delete expense');
    }
  };

  const updateBudgetStatus = async (status) => {
    try {
      setSaving(true);
      await apiClient.put(`/api/budget-v2/budgets/${budgetId}`, { status });
      fetchBudget();
    } catch (error) {
      console.error('Error updating budget status:', error);
      alert('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const fetchFinancialSummaryBudgets = async () => {
    try {
      setLoadingFinancialBudgets(true);
      const response = await apiClient.get(`/api/budget-v2/budgets/${budgetId}/financial-summary`);
      if (response && response.success) {
        // Convert from dollars to millions for display
        const toMillions = (val) => val ? (parseFloat(val) / 1000000).toFixed(2) : '';
        setFinancialSummaryBudgets({
          income: {
            jan: toMillions(response.income?.jan), feb: toMillions(response.income?.feb), mar: toMillions(response.income?.mar),
            apr: toMillions(response.income?.apr), may: toMillions(response.income?.may), jun: toMillions(response.income?.jun),
            jul: toMillions(response.income?.jul), aug: toMillions(response.income?.aug), sep: toMillions(response.income?.sep),
            oct: toMillions(response.income?.oct), nov: toMillions(response.income?.nov), dec: toMillions(response.income?.dec)
          },
          cogs: {
            jan: toMillions(response.cogs?.jan), feb: toMillions(response.cogs?.feb), mar: toMillions(response.cogs?.mar),
            apr: toMillions(response.cogs?.apr), may: toMillions(response.cogs?.may), jun: toMillions(response.cogs?.jun),
            jul: toMillions(response.cogs?.jul), aug: toMillions(response.cogs?.aug), sep: toMillions(response.cogs?.sep),
            oct: toMillions(response.cogs?.oct), nov: toMillions(response.cogs?.nov), dec: toMillions(response.cogs?.dec)
          }
        });
      }
    } catch (error) {
      console.error('Error fetching financial summary budgets:', error);
    } finally {
      setLoadingFinancialBudgets(false);
    }
  };

  const saveFinancialSummaryBudgets = async () => {
    try {
      setSaving(true);
      // Convert from millions to dollars for storage
      const toDollars = (val) => (parseFloat(val) || 0) * 1000000;

      const data = {
        income_jan: toDollars(financialSummaryBudgets.income.jan),
        income_feb: toDollars(financialSummaryBudgets.income.feb),
        income_mar: toDollars(financialSummaryBudgets.income.mar),
        income_apr: toDollars(financialSummaryBudgets.income.apr),
        income_may: toDollars(financialSummaryBudgets.income.may),
        income_jun: toDollars(financialSummaryBudgets.income.jun),
        income_jul: toDollars(financialSummaryBudgets.income.jul),
        income_aug: toDollars(financialSummaryBudgets.income.aug),
        income_sep: toDollars(financialSummaryBudgets.income.sep),
        income_oct: toDollars(financialSummaryBudgets.income.oct),
        income_nov: toDollars(financialSummaryBudgets.income.nov),
        income_dec: toDollars(financialSummaryBudgets.income.dec),
        cogs_jan: toDollars(financialSummaryBudgets.cogs.jan),
        cogs_feb: toDollars(financialSummaryBudgets.cogs.feb),
        cogs_mar: toDollars(financialSummaryBudgets.cogs.mar),
        cogs_apr: toDollars(financialSummaryBudgets.cogs.apr),
        cogs_may: toDollars(financialSummaryBudgets.cogs.may),
        cogs_jun: toDollars(financialSummaryBudgets.cogs.jun),
        cogs_jul: toDollars(financialSummaryBudgets.cogs.jul),
        cogs_aug: toDollars(financialSummaryBudgets.cogs.aug),
        cogs_sep: toDollars(financialSummaryBudgets.cogs.sep),
        cogs_oct: toDollars(financialSummaryBudgets.cogs.oct),
        cogs_nov: toDollars(financialSummaryBudgets.cogs.nov),
        cogs_dec: toDollars(financialSummaryBudgets.cogs.dec)
      };

      await apiClient.put(`/api/budget-v2/budgets/${budgetId}/financial-summary`, data);
      setShowFinancialSummaryModal(false);
      fetchMonthlyData(); // Refresh to show updated budgets
    } catch (error) {
      console.error('Error saving financial summary budgets:', error);
      alert('Failed to save financial summary budgets');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </MainLayout>
    );
  }

  if (!budget) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Budget not found</p>
          <Button onClick={() => navigate('/budget-agent-v2')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/budget-agent-v2')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-emerald-500" />
                {budget.name}
              </h1>
              <p className="text-gray-500">
                Fiscal Year: {budget.fiscal_year} | {budget.category_count || 0} Categories
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Badge className={`${
              budget.status === 'active' ? 'bg-green-500' :
              budget.status === 'completed' ? 'bg-blue-500' : 'bg-gray-500'
            } text-white`}>
              {budget.status || 'Draft'}
            </Badge>

            {budget.status === 'draft' && (
              <Button
                onClick={() => updateBudgetStatus('active')}
                className="bg-green-600 hover:bg-green-700"
                disabled={saving}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Activate
              </Button>
            )}

            <Button
              variant="outline"
              onClick={fetchBudget}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary Card with Budget vs Actual */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Period Selector */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-500">Period:</span>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="current_month">Current Month</option>
                  <option value="ytd">Year to Date</option>
                </select>
                {budgetVsActual?.summary?.period_label && (
                  <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                    {budgetVsActual.summary.period_label}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'monthly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('monthly')}
                  className={viewMode === 'monthly' ? 'bg-purple-600' : ''}
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Monthly
                </Button>
                <Button
                  variant={viewMode === 'vsActual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('vsActual')}
                  className={viewMode === 'vsActual' ? 'bg-emerald-600' : ''}
                >
                  <PieChart className="w-4 h-4 mr-1" />
                  Summary
                </Button>
                <Button
                  variant={viewMode === 'details' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('details')}
                  className={viewMode === 'details' ? 'bg-blue-600' : ''}
                >
                  <FolderOpen className="w-4 h-4 mr-1" />
                  Details
                </Button>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Total Budget</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(budgetVsActual?.summary?.total_budget || budget.total_amount)}
                </p>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Actual Spend</p>
                <p className="text-2xl font-bold text-blue-600">
                  {loadingActuals ? (
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    formatCurrency(budgetVsActual?.summary?.total_actual || 0)
                  )}
                </p>
              </div>
              <div className={`text-center p-4 rounded-lg ${
                (budgetVsActual?.summary?.total_variance || 0) >= 0
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                <p className="text-sm text-gray-500 mb-1">Variance</p>
                <p className={`text-2xl font-bold flex items-center justify-center gap-1 ${
                  (budgetVsActual?.summary?.total_variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {(budgetVsActual?.summary?.total_variance || 0) >= 0 ? (
                    <TrendingDown className="w-5 h-5" />
                  ) : (
                    <TrendingUp className="w-5 h-5" />
                  )}
                  {formatCurrency(Math.abs(budgetVsActual?.summary?.total_variance || 0))}
                </p>
                <p className="text-xs text-gray-400">
                  {(budgetVsActual?.summary?.total_variance || 0) >= 0 ? 'Under Budget' : 'Over Budget'}
                </p>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Categories</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {budgetVsActual?.summary?.category_count || budget.categories?.length || 0}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            {budgetVsActual?.summary?.total_budget > 0 && (
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Budget Utilization</span>
                  <span className={`font-medium ${
                    (budgetVsActual?.summary?.total_actual / budgetVsActual?.summary?.total_budget * 100) > 100
                      ? 'text-red-600'
                      : (budgetVsActual?.summary?.total_actual / budgetVsActual?.summary?.total_budget * 100) > 90
                        ? 'text-amber-600'
                        : 'text-emerald-600'
                  }`}>
                    {Math.round((budgetVsActual?.summary?.total_actual || 0) / budgetVsActual?.summary?.total_budget * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      (budgetVsActual?.summary?.total_actual / budgetVsActual?.summary?.total_budget * 100) > 100
                        ? 'bg-red-500'
                        : (budgetVsActual?.summary?.total_actual / budgetVsActual?.summary?.total_budget * 100) > 90
                          ? 'bg-amber-500'
                          : 'bg-emerald-500'
                    }`}
                    style={{
                      width: `${Math.min((budgetVsActual?.summary?.total_actual || 0) / budgetVsActual?.summary?.total_budget * 100, 100)}%`
                    }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Category and Financial Summary Buttons */}
        <div className="flex justify-end gap-3 mb-4">
          <Button
            onClick={() => {
              setShowFinancialSummaryModal(true);
              fetchFinancialSummaryBudgets();
            }}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Income/COGS Budget
          </Button>
          <Button
            onClick={() => setShowAddCategory(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>

        {/* Monthly Budget vs Actual View */}
        {viewMode === 'monthly' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 space-y-4">
              {/* Title and Filters Row */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Budget vs Actual Report
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Fiscal Year {monthlyData?.fiscal_year || budget?.fiscal_year} • {budget?.name}
                  </p>
                </div>
                {/* Year and Month Range Filter */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Year Filter */}
                  <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded-lg">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Year:</span>
                    <select
                      value={selectedYear || budget?.fiscal_year || ''}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-600 rounded-md text-sm font-bold text-blue-700 dark:text-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {availableYears.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>

                  {/* Month Range Filter */}
                  <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 rounded-lg">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Period:</span>
                    <select
                      value={startMonth}
                      onChange={(e) => setStartMonth(e.target.value)}
                      className="px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {allMonths.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span className="text-gray-400">→</span>
                    <select
                      value={endMonth}
                      onChange={(e) => setEndMonth(e.target.value)}
                      className="px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {allMonths.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => { setStartMonth('Jan'); setEndMonth('Dec'); }}
                      className="px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      Full Year
                    </button>
                  </div>
                </div>
              </div>

              {/* Export & Action Buttons Row */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Export Budget vs Actual Button */}
                <button
                  onClick={exportToExcel}
                  disabled={exporting || loadingMonthly}
                  className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg shadow-sm transition-colors"
                >
                  {exporting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Budget vs Actual</span>
                    </>
                  )}
                </button>

                {/* Export Budget Only Button */}
                <button
                  onClick={exportBudgetOnly}
                  disabled={exportingBudgetOnly}
                  className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium rounded-lg shadow-sm transition-colors"
                >
                  {exportingBudgetOnly ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Budget Only</span>
                    </>
                  )}
                </button>

                {/* Export Actual vs Actual Button */}
                <button
                  onClick={() => setShowActualVsActualModal(true)}
                  disabled={exportingActualVsActual || loadingMonthly}
                  className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-medium rounded-lg shadow-sm transition-colors"
                >
                  {exportingActualVsActual ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-4 h-4" />
                      <span>Actual vs Actual</span>
                    </>
                  )}
                </button>

                {/* Sync to NetSuite Button */}
                <button
                  onClick={syncToNetSuite}
                  disabled={syncing || loadingMonthly}
                  className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg shadow-sm transition-colors"
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <CloudUpload className="w-4 h-4" />
                      <span>Sync to NetSuite</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Table Content */}
            <div className="p-4">
              {loadingMonthly ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                  <span className="ml-3 text-gray-500 font-medium">Loading financial data...</span>
                </div>
              ) : monthlyData?.categories ? (
                (() => {
                  const startIdx = allMonths.indexOf(startMonth);
                  const endIdx = allMonths.indexOf(endMonth);
                  const filteredMonths = allMonths.slice(
                    Math.min(startIdx, endIdx),
                    Math.max(startIdx, endIdx) + 1
                  );

                  const getFilteredTotal = (monthlyObj) => {
                    return filteredMonths.reduce((sum, m) => sum + (monthlyObj?.[m] || 0), 0);
                  };

                  const formatAmount = (amount) => {
                    // Handle null, undefined, or non-numeric values
                    if (amount === null || amount === undefined || typeof amount === 'object') {
                      return '0.00';
                    }
                    const num = Number(amount) || 0;
                    // Always show in millions with 2 decimal places (no $ or M - unit shown in header)
                    return (num/1000000).toFixed(2);
                  };

                  return (
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                      <table className="w-full">
                        {/* Month Headers */}
                        <thead>
                          <tr className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                            <th className="text-left py-3 px-4 font-semibold text-sm sticky left-0 bg-slate-800 min-w-[220px] border-r border-slate-600">
                              Expense Category
                            </th>
                            {filteredMonths.map((month, idx) => (
                              <th key={month} colSpan="3" className={`text-center py-3 px-2 font-semibold text-sm ${idx > 0 ? 'border-l border-slate-600' : ''}`}>
                                {month} {monthlyData?.fiscal_year?.slice(-2)}
                              </th>
                            ))}
                            <th colSpan="3" className="text-center py-3 px-2 font-bold text-sm bg-indigo-700 border-l-2 border-indigo-500">
                              {filteredMonths.length === 1 ? 'Total' : filteredMonths.length <= 3 ? `${filteredMonths[0]}-${filteredMonths[filteredMonths.length-1]}` : 'Period Total'}
                            </th>
                          </tr>
                          {/* Sub Headers */}
                          <tr className="bg-gray-100 dark:bg-gray-700 text-xs font-semibold uppercase tracking-wider">
                            <th className="sticky left-0 bg-gray-100 dark:bg-gray-700 border-r border-gray-300 dark:border-gray-600"></th>
                            {filteredMonths.map((month, idx) => (
                              <React.Fragment key={`sub-${month}`}>
                                <th className={`py-2 px-2 text-center text-blue-700 dark:text-blue-400 ${idx > 0 ? 'border-l border-gray-300 dark:border-gray-600' : ''}`}>Actual</th>
                                <th className="py-2 px-2 text-center text-emerald-700 dark:text-emerald-400">Budget</th>
                                <th className="py-2 px-2 text-center text-gray-600 dark:text-gray-400">Variance</th>
                              </React.Fragment>
                            ))}
                            <th className="py-2 px-2 text-center text-blue-700 dark:text-blue-400 border-l-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/30">Actual</th>
                            <th className="py-2 px-2 text-center text-emerald-700 dark:text-emerald-400 bg-indigo-50 dark:bg-indigo-900/30">Budget</th>
                            <th className="py-2 px-2 text-center text-gray-600 dark:text-gray-400 bg-indigo-50 dark:bg-indigo-900/30">Variance</th>
                          </tr>
                          {/* Unit Row - $M indicator */}
                          <tr className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
                            <th className="sticky left-0 bg-gray-50 dark:bg-gray-800 border-r border-gray-300 dark:border-gray-600 py-1"></th>
                            {filteredMonths.map((month, idx) => (
                              <React.Fragment key={`unit-${month}`}>
                                <th className={`py-1 px-2 text-center font-normal ${idx > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''}`}>$M</th>
                                <th className="py-1 px-2 text-center font-normal">$M</th>
                                <th className="py-1 px-2 text-center font-normal">$M</th>
                              </React.Fragment>
                            ))}
                            <th className="py-1 px-2 text-center font-normal border-l-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/30">$M</th>
                            <th className="py-1 px-2 text-center font-normal bg-indigo-50 dark:bg-indigo-900/30">$M</th>
                            <th className="py-1 px-2 text-center font-normal bg-indigo-50 dark:bg-indigo-900/30">$M</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {/* Financial Summary Rows - Income, COGS, Gross Profit, GP Margin */}
                          {monthlyData.financial_summary && (
                            <>
                              {/* Total - Income Row */}
                              <tr className="bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors">
                                <td className="py-3 px-4 font-semibold text-emerald-800 dark:text-emerald-300 sticky left-0 bg-emerald-50 dark:bg-emerald-900/20 border-r border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    {monthlyData.financial_summary.income?.label || 'Total - Income'}
                                  </div>
                                </td>
                                {filteredMonths.map((month, mIdx) => {
                                  const actual = monthlyData.financial_summary.income?.monthly?.[month] || 0;
                                  const budget = monthlyData.financial_summary.income?.budget_monthly?.[month] || 0;
                                  const variance = budget > 0 ? actual - budget : 0;
                                  return (
                                    <React.Fragment key={`income-${month}`}>
                                      <td className={`py-3 px-2 text-right font-mono text-sm font-semibold text-emerald-700 dark:text-emerald-400 ${mIdx > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''}`}>
                                        {formatAmount(actual)}
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 dark:text-gray-400">
                                        {budget > 0 ? formatAmount(budget) : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {budget > 0 ? (variance >= 0 ? '+' : '') + formatAmount(variance) : '-'}
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                                {(() => {
                                  const incomeActual = getFilteredTotal(monthlyData.financial_summary.income?.monthly);
                                  const incomeBudget = getFilteredTotal(monthlyData.financial_summary.income?.budget_monthly);
                                  const incomeVariance = incomeActual - incomeBudget;
                                  return (
                                    <>
                                      <td className="py-3 px-2 text-right font-mono text-sm font-bold text-emerald-700 dark:text-emerald-400 border-l-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/30">
                                        {formatAmount(incomeActual)}
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 bg-indigo-50 dark:bg-indigo-900/30">
                                        {incomeBudget > 0 ? formatAmount(incomeBudget) : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm bg-indigo-50 dark:bg-indigo-900/30 ${incomeVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {incomeBudget > 0 ? (incomeVariance >= 0 ? '+' : '') + formatAmount(incomeVariance) : '-'}
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>

                              {/* Total - Cost Of Sales Row */}
                              <tr className="bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors">
                                <td className="py-3 px-4 font-semibold text-orange-800 dark:text-orange-300 sticky left-0 bg-orange-50 dark:bg-orange-900/20 border-r border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                    {monthlyData.financial_summary.cogs?.label || 'Total - Cost Of Sales'}
                                  </div>
                                </td>
                                {filteredMonths.map((month, mIdx) => {
                                  const actual = monthlyData.financial_summary.cogs?.monthly?.[month] || 0;
                                  const budget = monthlyData.financial_summary.cogs?.budget_monthly?.[month] || 0;
                                  const variance = budget > 0 ? budget - actual : 0;
                                  return (
                                    <React.Fragment key={`cogs-${month}`}>
                                      <td className={`py-3 px-2 text-right font-mono text-sm font-semibold text-orange-700 dark:text-orange-400 ${mIdx > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''}`}>
                                        {formatAmount(actual)}
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 dark:text-gray-400">
                                        {budget > 0 ? formatAmount(budget) : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {budget > 0 ? (variance >= 0 ? '+' : '') + formatAmount(variance) : '-'}
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                                {(() => {
                                  const cogsActual = getFilteredTotal(monthlyData.financial_summary.cogs?.monthly);
                                  const cogsBudget = getFilteredTotal(monthlyData.financial_summary.cogs?.budget_monthly);
                                  const cogsVariance = cogsBudget - cogsActual; // For expenses, positive variance = under budget
                                  return (
                                    <>
                                      <td className="py-3 px-2 text-right font-mono text-sm font-bold text-orange-700 dark:text-orange-400 border-l-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/30">
                                        {formatAmount(cogsActual)}
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 bg-indigo-50 dark:bg-indigo-900/30">
                                        {cogsBudget > 0 ? formatAmount(cogsBudget) : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm bg-indigo-50 dark:bg-indigo-900/30 ${cogsVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {cogsBudget > 0 ? (cogsVariance >= 0 ? '+' : '') + formatAmount(cogsVariance) : '-'}
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>

                              {/* Gross Profit Row */}
                              <tr className="bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border-t-2 border-blue-300 dark:border-blue-700">
                                <td className="py-3 px-4 font-bold text-blue-800 dark:text-blue-300 sticky left-0 bg-blue-50 dark:bg-blue-900/20 border-r border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                    {monthlyData.financial_summary.gross_profit?.label || 'Gross Profit'}
                                  </div>
                                </td>
                                {filteredMonths.map((month, mIdx) => {
                                  const actual = monthlyData.financial_summary.gross_profit?.monthly?.[month] || 0;
                                  const budget = monthlyData.financial_summary.gross_profit?.budget_monthly?.[month] || 0;
                                  const variance = budget > 0 ? actual - budget : 0;
                                  return (
                                    <React.Fragment key={`gp-${month}`}>
                                      <td className={`py-3 px-2 text-right font-mono text-sm font-bold ${actual >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-red-600'} ${mIdx > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''}`}>
                                        {formatAmount(actual)}
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 dark:text-gray-400">
                                        {budget > 0 ? formatAmount(budget) : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {budget > 0 ? (variance >= 0 ? '+' : '') + formatAmount(variance) : '-'}
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                                {(() => {
                                  const gpActual = getFilteredTotal(monthlyData.financial_summary.gross_profit?.monthly);
                                  const gpBudget = getFilteredTotal(monthlyData.financial_summary.gross_profit?.budget_monthly);
                                  const gpVariance = gpActual - gpBudget;
                                  return (
                                    <>
                                      <td className={`py-3 px-2 text-right font-mono text-sm font-bold border-l-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 ${gpActual >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-red-600'}`}>
                                        {formatAmount(gpActual)}
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 bg-indigo-50 dark:bg-indigo-900/30">
                                        {gpBudget > 0 ? formatAmount(gpBudget) : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm bg-indigo-50 dark:bg-indigo-900/30 ${gpVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {gpBudget > 0 ? (gpVariance >= 0 ? '+' : '') + formatAmount(gpVariance) : '-'}
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>

                              {/* GP Margin Row */}
                              <tr className="bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                                <td className="py-3 px-4 font-semibold text-purple-800 dark:text-purple-300 sticky left-0 bg-purple-50 dark:bg-purple-900/20 border-r border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                    {monthlyData.financial_summary.gp_margin?.label || 'GP Margin'}
                                  </div>
                                </td>
                                {filteredMonths.map((month, mIdx) => {
                                  const actual = monthlyData.financial_summary.gp_margin?.monthly?.[month] || 0;
                                  const budget = monthlyData.financial_summary.gp_margin?.budget_monthly?.[month] || 0;
                                  const variance = budget > 0 ? actual - budget : 0;
                                  return (
                                    <React.Fragment key={`margin-${month}`}>
                                      <td className={`py-3 px-2 text-right font-mono text-sm font-semibold text-purple-700 dark:text-purple-400 ${mIdx > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''}`}>
                                        {actual.toFixed(1)}%
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 dark:text-gray-400">
                                        {budget > 0 ? `${budget.toFixed(1)}%` : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {budget > 0 ? (variance >= 0 ? '+' : '') + `${variance.toFixed(1)}%` : '-'}
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                                {(() => {
                                  // Calculate GP Margin % from selected months: (GP / Income) × 100
                                  const gpTotal = getFilteredTotal(monthlyData.financial_summary.gross_profit?.monthly);
                                  const incomeTotal = getFilteredTotal(monthlyData.financial_summary.income?.monthly);
                                  const gpBudgetTotal = getFilteredTotal(monthlyData.financial_summary.gross_profit?.budget_monthly);
                                  const incomeBudgetTotal = getFilteredTotal(monthlyData.financial_summary.income?.budget_monthly);

                                  const actualMargin = incomeTotal > 0 ? (gpTotal / incomeTotal) * 100 : 0;
                                  const budgetMargin = incomeBudgetTotal > 0 ? (gpBudgetTotal / incomeBudgetTotal) * 100 : 0;
                                  const marginVariance = actualMargin - budgetMargin;

                                  return (
                                    <>
                                      <td className="py-3 px-2 text-right font-mono text-sm font-bold text-purple-700 dark:text-purple-400 border-l-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/30">
                                        {actualMargin.toFixed(1)}%
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-500 bg-indigo-50 dark:bg-indigo-900/30">
                                        {budgetMargin > 0 ? `${budgetMargin.toFixed(1)}%` : '-'}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm bg-indigo-50 dark:bg-indigo-900/30 ${marginVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {budgetMargin > 0 ? (marginVariance >= 0 ? '+' : '') + `${marginVariance.toFixed(1)}%` : '-'}
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>

                              {/* Separator row before expenses */}
                              <tr className="bg-gray-200 dark:bg-gray-600">
                                <td colSpan={4 + filteredMonths.length * 3} className="py-2 px-4 text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                  Operating Expenses
                                </td>
                              </tr>
                            </>
                          )}

                          {monthlyData.categories.map((category, idx) => {
                            const filteredActual = getFilteredTotal(category.monthly_actuals);
                            // Sum monthly budgets for the selected range (Jan-Mar = Jan+Feb+Mar budgets)
                            const filteredBudget = typeof category.monthly_budget === 'object'
                              ? getFilteredTotal(category.monthly_budget)
                              : (category.monthly_budget || 0) * filteredMonths.length;
                            const filteredVariance = filteredBudget - filteredActual;

                            return (
                              <tr key={category.category_id} className={`hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
                                <td className={`py-3 px-4 font-medium text-gray-900 dark:text-white sticky left-0 border-r border-gray-200 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                    {category.name}
                                  </div>
                                </td>
                                {filteredMonths.map((month, mIdx) => {
                                  const actual = category.monthly_actuals?.[month] || 0;
                                  // monthly_budget is now an object with budget per month (rolling balance)
                                  const budgetAmt = typeof category.monthly_budget === 'object'
                                    ? (category.monthly_budget?.[month] || 0)
                                    : (category.monthly_budget || 0);
                                  const variance = budgetAmt - actual;
                                  return (
                                    <React.Fragment key={`${category.category_id}-${month}`}>
                                      <td className={`py-3 px-2 text-right font-mono text-sm ${mIdx > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${actual > 0 ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-400'}`}>
                                        {actual > 0 ? formatAmount(actual) : '-'}
                                      </td>
                                      <td className="py-3 px-2 text-right font-mono text-sm text-gray-600 dark:text-gray-400">
                                        {formatAmount(budgetAmt)}
                                      </td>
                                      <td className={`py-3 px-2 text-right font-mono text-sm font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {actual > 0 ? (variance >= 0 ? '+' : '') + formatAmount(variance) : '-'}
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                                {/* Period Total Columns */}
                                <td className="py-3 px-2 text-right font-mono text-sm font-bold text-gray-900 dark:text-white border-l-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/30">
                                  {formatAmount(filteredActual)}
                                </td>
                                <td className="py-3 px-2 text-right font-mono text-sm font-medium text-gray-600 dark:text-gray-400 bg-indigo-50 dark:bg-indigo-900/30">
                                  {formatAmount(filteredBudget)}
                                </td>
                                <td className={`py-3 px-2 text-right font-mono text-sm font-bold bg-indigo-50 dark:bg-indigo-900/30 ${filteredVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {(filteredVariance >= 0 ? '+' : '') + formatAmount(filteredVariance)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {/* Totals Footer */}
                        <tfoot>
                          {(() => {
                            const totalFilteredActual = getFilteredTotal(monthlyData.totals?.actual_by_month);
                            // Sum monthly budgets for the selected range (Jan-Mar = Jan+Feb+Mar budgets)
                            const totalFilteredBudget = getFilteredTotal(monthlyData.totals?.budget_by_month);
                            const totalFilteredVariance = totalFilteredBudget - totalFilteredActual;
                            return (
                              <>
                                {/* Total Expense Row */}
                                <tr className="bg-gradient-to-r from-slate-700 to-slate-600 text-white font-bold">
                                  <td className="py-4 px-4 sticky left-0 bg-slate-700 border-r border-slate-500">
                                    <span className="text-sm uppercase tracking-wider">Total - Expense</span>
                                  </td>
                                  {filteredMonths.map((month, mIdx) => {
                                    const actual = monthlyData.totals?.actual_by_month?.[month] || 0;
                                    const budget = monthlyData.totals?.budget_by_month?.[month] || 0;
                                    const variance = budget - actual;
                                    return (
                                      <React.Fragment key={`total-${month}`}>
                                        <td className={`py-4 px-2 text-right font-mono text-sm ${mIdx > 0 ? 'border-l border-slate-500' : ''}`}>
                                          {formatAmount(actual)}
                                        </td>
                                        <td className="py-4 px-2 text-right font-mono text-sm text-slate-300">
                                          {formatAmount(budget)}
                                        </td>
                                        <td className={`py-4 px-2 text-right font-mono text-sm ${variance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          {(variance >= 0 ? '+' : '') + formatAmount(variance)}
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                  <td className="py-4 px-2 text-right font-mono text-base border-l-2 border-indigo-400 bg-indigo-800">
                                    {formatAmount(totalFilteredActual)}
                                  </td>
                                  <td className="py-4 px-2 text-right font-mono text-base text-indigo-200 bg-indigo-800">
                                    {formatAmount(totalFilteredBudget)}
                                  </td>
                                  <td className={`py-4 px-2 text-right font-mono text-base bg-indigo-800 ${totalFilteredVariance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {(totalFilteredVariance >= 0 ? '+' : '') + formatAmount(totalFilteredVariance)}
                                  </td>
                                </tr>

                                {/* Net Ordinary Income Row */}
                                {monthlyData.financial_summary && (
                                  <tr className="bg-gradient-to-r from-indigo-800 to-indigo-700 text-white font-bold border-t-2 border-indigo-500">
                                    <td className="py-4 px-4 sticky left-0 bg-indigo-800 border-r border-indigo-600">
                                      <span className="text-sm uppercase tracking-wider">Net Ordinary Income</span>
                                    </td>
                                    {filteredMonths.map((month, mIdx) => {
                                      const actual = monthlyData.financial_summary.net_income?.monthly?.[month] || 0;
                                      const budget = monthlyData.financial_summary.net_income?.budget_monthly?.[month] || 0;
                                      const variance = budget !== 0 ? actual - budget : 0;
                                      return (
                                        <React.Fragment key={`net-${month}`}>
                                          <td className={`py-4 px-2 text-right font-mono text-sm font-bold ${mIdx > 0 ? 'border-l border-indigo-600' : ''} ${actual >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatAmount(actual)}
                                          </td>
                                          <td className="py-4 px-2 text-right font-mono text-sm text-indigo-300">
                                            {budget !== 0 ? formatAmount(budget) : '-'}
                                          </td>
                                          <td className={`py-4 px-2 text-right font-mono text-sm ${variance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {budget !== 0 ? (variance >= 0 ? '+' : '') + formatAmount(variance) : '-'}
                                          </td>
                                        </React.Fragment>
                                      );
                                    })}
                                    {(() => {
                                      const netActual = getFilteredTotal(monthlyData.financial_summary.net_income?.monthly);
                                      const netBudget = getFilteredTotal(monthlyData.financial_summary.net_income?.budget_monthly);
                                      const netVariance = netActual - netBudget;
                                      return (
                                        <>
                                          <td className={`py-4 px-2 text-right font-mono text-base border-l-2 border-yellow-400 bg-indigo-900 ${netActual >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatAmount(netActual)}
                                          </td>
                                          <td className="py-4 px-2 text-right font-mono text-sm text-indigo-300 bg-indigo-900">
                                            {netBudget !== 0 ? formatAmount(netBudget) : '-'}
                                          </td>
                                          <td className={`py-4 px-2 text-right font-mono text-sm bg-indigo-900 ${netVariance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {netBudget !== 0 ? (netVariance >= 0 ? '+' : '') + formatAmount(netVariance) : '-'}
                                          </td>
                                        </>
                                      );
                                    })()}
                                  </tr>
                                )}
                              </>
                            );
                          })()}
                        </tfoot>
                      </table>
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <BarChart3 className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="font-medium">No budget data available</p>
                  <p className="text-sm">Create categories and add expense accounts to see the report</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Categories - Budget vs Actual View */}
        {viewMode === 'vsActual' && budgetVsActual?.categories && (
          <div className="space-y-4">
            {budgetVsActual.categories.map((category) => {
              const utilizationPercent = category.budget_amount > 0
                ? Math.round(category.actual_amount / category.budget_amount * 100)
                : 0;

              return (
                <Card key={category.category_id} className={`border-l-4 ${
                  category.status === 'over_budget' ? 'border-l-red-500' :
                  category.status === 'near_limit' ? 'border-l-amber-500' :
                  category.status === 'under_budget' ? 'border-l-emerald-500' : 'border-l-gray-300'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <FolderOpen className={`w-5 h-5 ${
                          category.status === 'over_budget' ? 'text-red-500' :
                          category.status === 'near_limit' ? 'text-amber-500' : 'text-emerald-500'
                        }`} />
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {category.name}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {category.account_count} expense account(s)
                          </p>
                        </div>
                      </div>
                      <Badge className={`${
                        category.status === 'over_budget' ? 'bg-red-100 text-red-700' :
                        category.status === 'near_limit' ? 'bg-amber-100 text-amber-700' :
                        category.status === 'under_budget' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {category.status === 'over_budget' ? 'Over Budget' :
                         category.status === 'near_limit' ? 'Near Limit' :
                         category.status === 'under_budget' ? 'Under Budget' : 'No Budget'}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">Budget</p>
                        <p className="text-lg font-bold text-emerald-600">
                          {formatCurrency(category.budget_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Actual Spend</p>
                        <p className="text-lg font-bold text-blue-600">
                          {formatCurrency(category.actual_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Variance</p>
                        <p className={`text-lg font-bold flex items-center gap-1 ${
                          category.variance >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {category.variance >= 0 ? (
                            <TrendingDown className="w-4 h-4" />
                          ) : (
                            <TrendingUp className="w-4 h-4" />
                          )}
                          {formatCurrency(Math.abs(category.variance))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Utilization</p>
                        <p className={`text-lg font-bold ${
                          utilizationPercent > 100 ? 'text-red-600' :
                          utilizationPercent > 90 ? 'text-amber-600' : 'text-gray-900 dark:text-white'
                        }`}>
                          {utilizationPercent}%
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          utilizationPercent > 100 ? 'bg-red-500' :
                          utilizationPercent > 90 ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {budgetVsActual.categories.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <PieChart className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No categories to compare
                  </h3>
                  <p className="text-gray-500">
                    Add categories to see budget vs actual comparison
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Categories - Details View (Original) */}
        {viewMode === 'details' && (
          <>
            {(!budget.categories || budget.categories.length === 0) ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No categories yet
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Add categories to start building your budget
                  </p>
                  <Button
                    onClick={() => setShowAddCategory(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {budget.categories.map((category) => (
                  <Card key={category.category_id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FolderOpen className="w-5 h-5 text-emerald-500" />
                          {category.name}
                        </CardTitle>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-gray-500">Budget</p>
                            <p className="font-bold text-emerald-600">
                              {formatCurrency(category.approved_amount)}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setExpenseCategoryId(category.category_id);
                                setShowAddExpense(true);
                              }}
                              className="text-emerald-500"
                              title="Add Expense"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // Convert from dollars to millions for display
                                const toMillions = (val) => val ? (val / 1000000).toFixed(2) : '';
                                setEditCategoryData({
                                  category_id: category.category_id,
                                  name: category.name,
                                  description: category.description || '',
                                  jan_budget: toMillions(category.jan_budget),
                                  feb_budget: toMillions(category.feb_budget),
                                  mar_budget: toMillions(category.mar_budget),
                                  apr_budget: toMillions(category.apr_budget),
                                  may_budget: toMillions(category.may_budget),
                                  jun_budget: toMillions(category.jun_budget),
                                  jul_budget: toMillions(category.jul_budget),
                                  aug_budget: toMillions(category.aug_budget),
                                  sep_budget: toMillions(category.sep_budget),
                                  oct_budget: toMillions(category.oct_budget),
                                  nov_budget: toMillions(category.nov_budget),
                                  dec_budget: toMillions(category.dec_budget)
                                });
                                // Load existing accounts (expenses) linked to this category
                                setEditCategoryExistingAccounts(category.expenses || []);
                                setEditCategoryAccounts([]);
                                setEditAccountSearch('');
                                // Fetch expense accounts if not loaded
                                if (expenseAccounts.all.length === 0) {
                                  fetchExpenseAccounts();
                                }
                                setShowEditCategory(true);
                              }}
                              className="text-blue-500"
                              title="Edit Category"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteCategory(category.category_id)}
                              className="text-red-500"
                              title="Delete Category"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {category.expenses && category.expenses.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Expense Name</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead className="text-right w-20">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {category.expenses.map((expense) => (
                              <TableRow key={expense.line_item_id}>
                                <TableCell>
                                  {editingExpense === expense.line_item_id ? (
                                    <input
                                      type="text"
                                      defaultValue={expense.account_name}
                                      onBlur={(e) => updateExpense(expense.line_item_id, { name: e.target.value })}
                                      className="w-full px-2 py-1 border rounded"
                                      autoFocus
                                    />
                                  ) : (
                                    expense.account_name
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {editingExpense === expense.line_item_id ? (
                                    <input
                                      type="number"
                                      defaultValue={expense.approved_amount || expense.amount}
                                      onBlur={(e) => updateExpense(expense.line_item_id, { amount: parseFloat(e.target.value) })}
                                      className="w-24 px-2 py-1 border rounded text-right"
                                    />
                                  ) : (
                                    formatCurrency(expense.approved_amount || expense.amount)
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setEditingExpense(
                                        editingExpense === expense.line_item_id ? null : expense.line_item_id
                                      )}
                                      className="text-gray-500"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteExpense(expense.line_item_id)}
                                      className="text-red-500"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-gray-500 text-sm py-4 text-center">
                          No expenses in this category.
                          <button
                            onClick={() => {
                              setExpenseCategoryId(category.category_id);
                              setShowAddExpense(true);
                            }}
                            className="text-emerald-600 ml-1 hover:underline"
                          >
                            Add one
                          </button>
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Add Category Modal */}
        {showAddCategory && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-5xl max-h-[90vh] overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Add Category</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setShowAddCategory(false);
                    setNewCategory({
                      name: '', description: '', budget_amount: '',
                      jan_budget: '', feb_budget: '', mar_budget: '',
                      apr_budget: '', may_budget: '', jun_budget: '',
                      jul_budget: '', aug_budget: '', sep_budget: '',
                      oct_budget: '', nov_budget: '', dec_budget: ''
                    });
                    setSelectedCategoryAccounts([]);
                    setCategoryAccountSearch('');
                  }}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 overflow-y-auto max-h-[70vh]">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Category Name *</label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                      placeholder="e.g., Marketing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <input
                      type="text"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                      placeholder="Optional description"
                    />
                  </div>
                </div>

                {/* Monthly Budget Inputs - Values in Millions */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium">Monthly Budget <span className="text-emerald-600">($M)</span></label>
                    <span className="text-xs text-gray-500">
                      Total: {Object.keys(newCategory)
                        .filter(k => k.endsWith('_budget') && k !== 'budget_amount')
                        .reduce((sum, k) => sum + (parseFloat(newCategory[k]) || 0), 0)
                        .toFixed(2)}M
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { key: 'jan_budget', label: 'Jan' },
                      { key: 'feb_budget', label: 'Feb' },
                      { key: 'mar_budget', label: 'Mar' },
                      { key: 'apr_budget', label: 'Apr' },
                      { key: 'may_budget', label: 'May' },
                      { key: 'jun_budget', label: 'Jun' },
                      { key: 'jul_budget', label: 'Jul' },
                      { key: 'aug_budget', label: 'Aug' },
                      { key: 'sep_budget', label: 'Sep' },
                      { key: 'oct_budget', label: 'Oct' },
                      { key: 'nov_budget', label: 'Nov' },
                      { key: 'dec_budget', label: 'Dec' }
                    ].map(({ key, label }) => (
                      <div key={key} className="relative">
                        <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newCategory[key]}
                          onChange={(e) => setNewCategory({ ...newCategory, [key]: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                          placeholder="0.00"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expense Accounts Section */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-2">
                    Link Expense Accounts (Optional)
                    {selectedCategoryAccounts.length > 0 && (
                      <span className="ml-2 text-emerald-600">({selectedCategoryAccounts.length} selected)</span>
                    )}
                  </label>

                  {/* Account Type Filter */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setCategoryAccountType('all')}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        categoryAccountType === 'all'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      All ({expenseAccounts.all.length})
                    </button>
                    <button
                      onClick={() => setCategoryAccountType('summary')}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        categoryAccountType === 'summary'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Parent ({expenseAccounts.summary.length})
                    </button>
                    <button
                      onClick={() => setCategoryAccountType('detail')}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        categoryAccountType === 'detail'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Child ({expenseAccounts.detail.length})
                    </button>
                  </div>

                  {/* Search */}
                  <input
                    type="text"
                    value={categoryAccountSearch}
                    onChange={(e) => setCategoryAccountSearch(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 mb-3"
                    placeholder="Search accounts..."
                  />

                  {/* Account List */}
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {loadingAccounts ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                        <span className="ml-2 text-gray-500">Loading accounts...</span>
                      </div>
                    ) : (
                      (() => {
                        const accountList = categoryAccountType === 'summary'
                          ? expenseAccounts.summary
                          : categoryAccountType === 'detail'
                            ? expenseAccounts.detail
                            : expenseAccounts.all;

                        const filtered = accountList.filter(acc =>
                          !categoryAccountSearch ||
                          (acc.acctname || acc.fullname || acc.name || '').toLowerCase().includes(categoryAccountSearch.toLowerCase()) ||
                          (acc.acctnumber || '').toLowerCase().includes(categoryAccountSearch.toLowerCase())
                        );

                        return filtered.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">No accounts found</div>
                        ) : (
                          filtered.map(account => {
                            const isSelected = selectedCategoryAccounts.some(a => a.id === account.id);
                            const accountName = account.acctname || account.fullname || account.name || '';
                            return (
                              <div
                                key={account.id}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedCategoryAccounts(prev => prev.filter(a => a.id !== account.id));
                                  } else {
                                    setSelectedCategoryAccounts(prev => [...prev, account]);
                                  }
                                }}
                                className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b last:border-b-0 ${
                                  isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="w-4 h-4 text-emerald-600 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">
                                    {accountName}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        );
                      })()
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => {
                    setShowAddCategory(false);
                    setNewCategory({
                      name: '', description: '', budget_amount: '',
                      jan_budget: '', feb_budget: '', mar_budget: '',
                      apr_budget: '', may_budget: '', jun_budget: '',
                      jul_budget: '', aug_budget: '', sep_budget: '',
                      oct_budget: '', nov_budget: '', dec_budget: ''
                    });
                    setSelectedCategoryAccounts([]);
                    setCategoryAccountSearch('');
                  }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={addCategory}
                    disabled={saving || !newCategory.name.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Add Category'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actual vs Actual Export Modal */}
        {showActualVsActualModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500">
                <CardTitle className="flex items-center justify-between text-white">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Export Actual vs Actual
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setShowActualVsActualModal(false)} className="text-white hover:bg-white/20">
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-6">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Compare actual performance between two periods. Select months or quarters to compare.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Period 1
                    </label>
                    <select
                      value={actualVsActualPeriod1}
                      onChange={(e) => setActualVsActualPeriod1(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500"
                    >
                      <optgroup label="Quarters">
                        <option value="Q1">Q1 (Jan-Mar)</option>
                        <option value="Q2">Q2 (Apr-Jun)</option>
                        <option value="Q3">Q3 (Jul-Sep)</option>
                        <option value="Q4">Q4 (Oct-Dec)</option>
                      </optgroup>
                      <optgroup label="Months">
                        <option value="Jan">January</option>
                        <option value="Feb">February</option>
                        <option value="Mar">March</option>
                        <option value="Apr">April</option>
                        <option value="May">May</option>
                        <option value="Jun">June</option>
                        <option value="Jul">July</option>
                        <option value="Aug">August</option>
                        <option value="Sep">September</option>
                        <option value="Oct">October</option>
                        <option value="Nov">November</option>
                        <option value="Dec">December</option>
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Period 2
                    </label>
                    <select
                      value={actualVsActualPeriod2}
                      onChange={(e) => setActualVsActualPeriod2(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500"
                    >
                      <optgroup label="Quarters">
                        <option value="Q1">Q1 (Jan-Mar)</option>
                        <option value="Q2">Q2 (Apr-Jun)</option>
                        <option value="Q3">Q3 (Jul-Sep)</option>
                        <option value="Q4">Q4 (Oct-Dec)</option>
                      </optgroup>
                      <optgroup label="Months">
                        <option value="Jan">January</option>
                        <option value="Feb">February</option>
                        <option value="Mar">March</option>
                        <option value="Apr">April</option>
                        <option value="May">May</option>
                        <option value="Jun">June</option>
                        <option value="Jul">July</option>
                        <option value="Aug">August</option>
                        <option value="Sep">September</option>
                        <option value="Oct">October</option>
                        <option value="Nov">November</option>
                        <option value="Dec">December</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm">
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Preview:</strong> Comparing <span className="text-orange-600 font-medium">{actualVsActualPeriod1}</span> vs <span className="text-orange-600 font-medium">{actualVsActualPeriod2}</span> for {selectedYear || budget?.fiscal_year || new Date().getFullYear()}
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowActualVsActualModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={exportActualVsActual}
                    disabled={exportingActualVsActual || actualVsActualPeriod1 === actualVsActualPeriod2}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {exportingActualVsActual ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel
                      </>
                    )}
                  </Button>
                </div>

                {actualVsActualPeriod1 === actualVsActualPeriod2 && (
                  <p className="text-xs text-red-500 text-center">
                    Please select different periods to compare
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Financial Summary Budget Modal */}
        {showFinancialSummaryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-5xl max-h-[90vh] overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600">
                <CardTitle className="flex items-center justify-between text-white">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Financial Summary Budgets
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setShowFinancialSummaryModal(false)} className="text-white hover:bg-white/20">
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 overflow-y-auto max-h-[70vh] p-6">
                {loadingFinancialBudgets ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
                    <span className="ml-3 text-gray-500">Loading budgets...</span>
                  </div>
                ) : (
                  <>
                    {/* Income Budget Row */}
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                          Total Income Budget <span className="text-sm font-normal text-emerald-600">($M)</span>
                        </h3>
                        <span className="text-sm font-medium text-emerald-600 bg-emerald-100 dark:bg-emerald-800 px-3 py-1 rounded-full">
                          Total: {Object.values(financialSummaryBudgets.income).reduce((sum, v) => sum + (parseFloat(v) || 0), 0).toFixed(2)}M
                        </span>
                      </div>
                      <div className="grid grid-cols-6 gap-3">
                        {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map((month) => (
                          <div key={`income-${month}`}>
                            <label className="block text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-1 uppercase">{month}</label>
                            <input
                              type="number"
                              step="0.01"
                              value={financialSummaryBudgets.income[month]}
                              onChange={(e) => setFinancialSummaryBudgets(prev => ({
                                ...prev,
                                income: { ...prev.income, [month]: e.target.value }
                              }))}
                              className="w-full px-2 py-2 text-sm border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-800 dark:border-emerald-600"
                              placeholder="0.00"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* COGS Budget Row */}
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-300 flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                          Cost of Sales (COGS) Budget <span className="text-sm font-normal text-orange-600">($M)</span>
                        </h3>
                        <span className="text-sm font-medium text-orange-600 bg-orange-100 dark:bg-orange-800 px-3 py-1 rounded-full">
                          Total: {Object.values(financialSummaryBudgets.cogs).reduce((sum, v) => sum + (parseFloat(v) || 0), 0).toFixed(2)}M
                        </span>
                      </div>
                      <div className="grid grid-cols-6 gap-3">
                        {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map((month) => (
                          <div key={`cogs-${month}`}>
                            <label className="block text-xs font-medium text-orange-700 dark:text-orange-400 mb-1 uppercase">{month}</label>
                            <input
                              type="number"
                              step="0.01"
                              value={financialSummaryBudgets.cogs[month]}
                              onChange={(e) => setFinancialSummaryBudgets(prev => ({
                                ...prev,
                                cogs: { ...prev.cogs, [month]: e.target.value }
                              }))}
                              className="w-full px-2 py-2 text-sm border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:border-orange-600"
                              placeholder="0.00"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Calculated Summary */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-5 border-2 border-blue-200 dark:border-blue-700">
                      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        Calculated Values (Auto-computed)
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-500 mb-1">Gross Profit (Income - COGS)</p>
                          <p className="text-2xl font-bold text-blue-600">
                            ${(Object.values(financialSummaryBudgets.income).reduce((sum, v) => sum + (parseFloat(v) || 0), 0) -
                              Object.values(financialSummaryBudgets.cogs).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)).toFixed(2)}M
                          </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-500 mb-1">GP Margin %</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {(() => {
                              const income = Object.values(financialSummaryBudgets.income).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
                              const cogs = Object.values(financialSummaryBudgets.cogs).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
                              return income > 0 ? (((income - cogs) / income) * 100).toFixed(1) : '0.0';
                            })()}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowFinancialSummaryModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={saveFinancialSummaryBudgets}
                    disabled={saving || loadingFinancialBudgets}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Budgets
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Category Modal */}
        {showEditCategory && editCategoryData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Edit Category: {editCategoryData.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setShowEditCategory(false);
                    setEditCategoryData(null);
                  }}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 overflow-y-auto max-h-[70vh]">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Category Name *</label>
                    <input
                      type="text"
                      value={editCategoryData.name}
                      onChange={(e) => setEditCategoryData({ ...editCategoryData, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <input
                      type="text"
                      value={editCategoryData.description}
                      onChange={(e) => setEditCategoryData({ ...editCategoryData, description: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                </div>

                {/* Monthly Budget Inputs - Values in Millions */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium">Monthly Budget <span className="text-emerald-600">($M)</span></label>
                    <span className="text-xs text-gray-500">
                      Total: {['jan_budget', 'feb_budget', 'mar_budget', 'apr_budget', 'may_budget', 'jun_budget',
                               'jul_budget', 'aug_budget', 'sep_budget', 'oct_budget', 'nov_budget', 'dec_budget']
                        .reduce((sum, k) => sum + (parseFloat(editCategoryData[k]) || 0), 0)
                        .toFixed(2)}M
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { key: 'jan_budget', label: 'Jan' },
                      { key: 'feb_budget', label: 'Feb' },
                      { key: 'mar_budget', label: 'Mar' },
                      { key: 'apr_budget', label: 'Apr' },
                      { key: 'may_budget', label: 'May' },
                      { key: 'jun_budget', label: 'Jun' },
                      { key: 'jul_budget', label: 'Jul' },
                      { key: 'aug_budget', label: 'Aug' },
                      { key: 'sep_budget', label: 'Sep' },
                      { key: 'oct_budget', label: 'Oct' },
                      { key: 'nov_budget', label: 'Nov' },
                      { key: 'dec_budget', label: 'Dec' }
                    ].map(({ key, label }) => (
                      <div key={key} className="relative">
                        <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editCategoryData[key]}
                          onChange={(e) => setEditCategoryData({ ...editCategoryData, [key]: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                          placeholder="0.00"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Linked Accounts Section */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-2">
                    Linked Expense Accounts
                    <span className="ml-2 text-gray-500">
                      ({editCategoryExistingAccounts.length} existing{editCategoryAccounts.length > 0 ? `, ${editCategoryAccounts.length} new` : ''})
                    </span>
                  </label>

                  {/* Existing Accounts */}
                  {editCategoryExistingAccounts.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Existing accounts:</p>
                      <div className="flex flex-wrap gap-2">
                        {editCategoryExistingAccounts.map((acc) => (
                          <span
                            key={acc.line_item_id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm"
                          >
                            {acc.account_name || acc.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Accounts to Add */}
                  {editCategoryAccounts.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-emerald-600 mb-1">New accounts to add:</p>
                      <div className="flex flex-wrap gap-2">
                        {editCategoryAccounts.map((acc) => (
                          <span
                            key={acc.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900 rounded text-sm"
                          >
                            {acc.acctname || acc.fullname}
                            <button
                              onClick={() => setEditCategoryAccounts(editCategoryAccounts.filter(a => a.id !== acc.id))}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Search and Add Accounts */}
                  <div className="relative">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={editAccountSearch}
                        onChange={(e) => setEditAccountSearch(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                        placeholder="Search expense accounts to add..."
                      />
                    </div>
                    {editAccountSearch && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {expenseAccounts.all
                          .filter(acc => {
                            const search = editAccountSearch.toLowerCase();
                            const matchesSearch = (acc.acctname || '').toLowerCase().includes(search) ||
                                                  (acc.acctnumber || '').toLowerCase().includes(search);
                            // Filter out already linked accounts
                            const existingIds = editCategoryExistingAccounts.map(e => e.netsuite_account_id?.toString());
                            const newIds = editCategoryAccounts.map(e => e.id?.toString());
                            const notAlreadyAdded = !existingIds.includes(acc.id?.toString()) && !newIds.includes(acc.id?.toString());
                            return matchesSearch && notAlreadyAdded;
                          })
                          .slice(0, 10)
                          .map(acc => (
                            <button
                              key={acc.id}
                              onClick={() => {
                                setEditCategoryAccounts([...editCategoryAccounts, acc]);
                                setEditAccountSearch('');
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center"
                            >
                              <span>{acc.acctnumber} - {acc.acctname}</span>
                              <Plus className="w-4 h-4 text-emerald-500" />
                            </button>
                          ))}
                        {expenseAccounts.all.filter(acc => {
                          const search = editAccountSearch.toLowerCase();
                          return (acc.acctname || '').toLowerCase().includes(search) ||
                                 (acc.acctnumber || '').toLowerCase().includes(search);
                        }).length === 0 && (
                          <p className="px-3 py-2 text-gray-500 text-sm">No accounts found</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEditCategory(false);
                      setEditCategoryData(null);
                      setEditCategoryAccounts([]);
                      setEditCategoryExistingAccounts([]);
                      setEditAccountSearch('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        setSaving(true);
                        // Convert from millions to dollars
                        const toMillions = (val) => (parseFloat(val) || 0) * 1000000;
                        const data = {
                          name: editCategoryData.name,
                          description: editCategoryData.description,
                          jan_budget: toMillions(editCategoryData.jan_budget),
                          feb_budget: toMillions(editCategoryData.feb_budget),
                          mar_budget: toMillions(editCategoryData.mar_budget),
                          apr_budget: toMillions(editCategoryData.apr_budget),
                          may_budget: toMillions(editCategoryData.may_budget),
                          jun_budget: toMillions(editCategoryData.jun_budget),
                          jul_budget: toMillions(editCategoryData.jul_budget),
                          aug_budget: toMillions(editCategoryData.aug_budget),
                          sep_budget: toMillions(editCategoryData.sep_budget),
                          oct_budget: toMillions(editCategoryData.oct_budget),
                          nov_budget: toMillions(editCategoryData.nov_budget),
                          dec_budget: toMillions(editCategoryData.dec_budget)
                        };
                        await updateCategory(editCategoryData.category_id, data);

                        // Add new accounts to the category
                        for (const account of editCategoryAccounts) {
                          const accountName = account.acctname || account.fullname || account.name;
                          await apiClient.post(`/api/budget-v2/categories/${editCategoryData.category_id}/expenses`, {
                            name: accountName,
                            account_id: account.id,
                            account_name: accountName,
                            account_number: account.acctnumber,
                            amount: 0
                          });
                        }

                        setShowEditCategory(false);
                        setEditCategoryData(null);
                        setEditCategoryAccounts([]);
                        setEditCategoryExistingAccounts([]);
                        setEditAccountSearch('');
                        fetchBudget();
                        fetchMonthlyData();
                      } catch (error) {
                        console.error('Error updating category:', error);
                        alert('Failed to update category');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving || !editCategoryData.name.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Expense Modal */}
        {showAddExpense && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-5xl max-h-[90vh] overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Add Expense Account</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setShowAddExpense(false);
                    setExpenseCategoryId(null);
                    setNewExpense({
                      name: '', amount: '', account_id: '',
                      jan_budget: '', feb_budget: '', mar_budget: '',
                      apr_budget: '', may_budget: '', jun_budget: '',
                      jul_budget: '', aug_budget: '', sep_budget: '',
                      oct_budget: '', nov_budget: '', dec_budget: ''
                    });
                    setAccountSearch('');
                  }}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 overflow-y-auto max-h-[70vh]">
                {/* Account Type Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Account Type</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAccountType('all')}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        showAccountType === 'all'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      All ({expenseAccounts.all.length})
                    </button>
                    <button
                      onClick={() => setShowAccountType('summary')}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        showAccountType === 'summary'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Summary ({expenseAccounts.summary.length})
                    </button>
                    <button
                      onClick={() => setShowAccountType('detail')}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        showAccountType === 'detail'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Detail ({expenseAccounts.detail.length})
                    </button>
                  </div>
                </div>

                {/* Search Accounts */}
                <div>
                  <label className="block text-sm font-medium mb-1">Search Expense Accounts</label>
                  <input
                    type="text"
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                    placeholder="Search by account name or number..."
                  />
                </div>

                {/* Account List */}
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {loadingAccounts ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Loading accounts...</span>
                    </div>
                  ) : getFilteredAccounts().length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {expenseAccounts.all.length === 0
                        ? 'No expense accounts found. Check NetSuite connection.'
                        : 'No accounts match your search.'}
                    </div>
                  ) : (
                    <div className="divide-y dark:divide-gray-700">
                      {getFilteredAccounts().slice(0, 50).map((account) => (
                        <button
                          key={account.id}
                          onClick={() => selectAccount(account)}
                          className={`w-full px-3 py-2 text-left hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors ${
                            newExpense.account_id === account.id ? 'bg-emerald-100 dark:bg-emerald-900/30' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white text-sm">
                                {account.acctname}
                              </p>
                              <p className="text-xs text-gray-500">
                                {account.acctnumber} • {account.accttype}
                                {account.account_level === 'summary' && (
                                  <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Summary</Badge>
                                )}
                              </p>
                            </div>
                            {newExpense.account_id === account.id && (
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            )}
                          </div>
                        </button>
                      ))}
                      {getFilteredAccounts().length > 50 && (
                        <p className="text-center py-2 text-xs text-gray-500">
                          Showing first 50 results. Refine your search.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Account */}
                {newExpense.name && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Selected Account:</p>
                    <p className="font-medium text-emerald-700 dark:text-emerald-300">{newExpense.name}</p>
                  </div>
                )}

                {/* Or Enter Custom */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-1">Or Enter Custom Expense Name</label>
                  <input
                    type="text"
                    value={newExpense.name}
                    onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value, account_id: '' })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                    placeholder="e.g., Custom Expense Item"
                  />
                </div>

                {/* Monthly Budget Inputs - Values in Millions */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium">Monthly Budget <span className="text-emerald-600">($M)</span></label>
                    <span className="text-xs text-gray-500">
                      Total: {Object.keys(newExpense)
                        .filter(k => k.endsWith('_budget'))
                        .reduce((sum, k) => sum + (parseFloat(newExpense[k]) || 0), 0)
                        .toFixed(2)}M
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { key: 'jan_budget', label: 'Jan' },
                      { key: 'feb_budget', label: 'Feb' },
                      { key: 'mar_budget', label: 'Mar' },
                      { key: 'apr_budget', label: 'Apr' },
                      { key: 'may_budget', label: 'May' },
                      { key: 'jun_budget', label: 'Jun' },
                      { key: 'jul_budget', label: 'Jul' },
                      { key: 'aug_budget', label: 'Aug' },
                      { key: 'sep_budget', label: 'Sep' },
                      { key: 'oct_budget', label: 'Oct' },
                      { key: 'nov_budget', label: 'Nov' },
                      { key: 'dec_budget', label: 'Dec' }
                    ].map(({ key, label }) => (
                      <div key={key} className="relative">
                        <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newExpense[key]}
                          onChange={(e) => setNewExpense({ ...newExpense, [key]: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                          placeholder="0.00"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => {
                    setShowAddExpense(false);
                    setExpenseCategoryId(null);
                    setNewExpense({
                      name: '', amount: '', account_id: '',
                      jan_budget: '', feb_budget: '', mar_budget: '',
                      apr_budget: '', may_budget: '', jun_budget: '',
                      jul_budget: '', aug_budget: '', sep_budget: '',
                      oct_budget: '', nov_budget: '', dec_budget: ''
                    });
                    setAccountSearch('');
                  }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={addExpense}
                    disabled={saving || !newExpense.name.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Add Expense'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BudgetAgentV2Detail;
